import * as THREE from 'three';
import type {} from '@react-three/fiber';
import { allSquares, powerOf, type SquarePower } from '@chessmate/game-engine';
import { squareToVec3 } from './scene.js';

/**
 * Square power — a wash showing which squares are worth taking, independent of
 * what stands on them.
 *
 * **Height is the whole trick.** `HeatMap` renders at y=0.05–0.06 and the
 * highlight rings sit at y=0.028–0.045. Copying HeatMap's height would stack
 * the wash *above* the selection and legal-move rings and mute them, so this
 * sits at y=0.02, below every ring. `depthWrite: false` is what actually
 * guarantees the layering; the y value only decides which draws first.
 *
 * **A single-hue ramp**, because power is an ordered quantity, not a
 * categorical one — three steps of one violet rather than three colours. That
 * also keeps it clear of the four hues the highlight system has already claimed
 * (green target, red capture, amber last move, cyan look-ahead) and avoids
 * teaching a kid that a fifth colour means a move.
 *
 * **Zero power renders nothing at all** — no plane, not a transparent one. It
 * declutters the 28 outer squares and makes "0" read as absence, which is the
 * thing a child should take away from an edge square.
 */

export const POWER_TINT: Record<Exclude<SquarePower, 0>, THREE.MeshBasicMaterial> = {
  2: new THREE.MeshBasicMaterial({ color: '#8a63d2', transparent: true, opacity: 0.18, depthWrite: false }),
  4: new THREE.MeshBasicMaterial({ color: '#8a63d2', transparent: true, opacity: 0.34, depthWrite: false }),
  6: new THREE.MeshBasicMaterial({ color: '#8a63d2', transparent: true, opacity: 0.5, depthWrite: false }),
};

/** One shared plane for all 36 tinted squares — no per-square geometry. */
const squarePlane = new THREE.PlaneGeometry(0.95, 0.95);

/**
 * The wash height, exported so the z-order claim in the module comment is
 * asserted rather than asserted-in-prose. `HeatMap` sits at 0.05/0.06 and the
 * highlight rings at 0.028–0.045, so 0.02 is below all of them; if a future
 * change drops a wash here the test fails instead of the rings going quiet.
 */
export const POWER_PLANE_Y = 0.02;

/**
 * The squares that get a plane at all. Computed once at module scope: the map
 * is a function of the coordinates alone, so it is identical for every position
 * and every FEN — recomputing it per render would be 64 pointless calls on each
 * board update.
 */
export type LitSquare = { square: string; power: Exclude<SquarePower, 0> };

export const LIT_SQUARES: LitSquare[] = allSquares()
  .map((square) => ({ square, power: powerOf(square) }))
  // An explicit predicate, not a bare `> 0`: a plain comparison filter does not
  // narrow, so the material lookup would be indexing with `0 | 2 | 4 | 6` and
  // there is no material for 0 — which is the whole point of dropping those.
  .filter((cell): cell is LitSquare => cell.power > 0);

export function PowerMap() {
  return (
    <group>
      {LIT_SQUARES.map(({ square, power }) => {
        const [x, , z] = squareToVec3(square);
        return (
          <mesh
            key={square}
            geometry={squarePlane}
            position={[x, POWER_PLANE_Y, z]}
            rotation={[-Math.PI / 2, 0, 0]}
            material={POWER_TINT[power]}
          />
        );
      })}
    </group>
  );
}
